
import React from "react";
import { LucideIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMediaQuery } from "@/hooks/use-media-query";

interface CardContactItemProps {
  icon: LucideIcon;
  content?: string;
  value?: string;  // For backward compatibility
  href?: string;
  onClick?: () => void;
  onTrackClick?: () => void;
  label?: string;
  type?: string;
  showTooltip?: boolean;
  maxWidth?: string;
}

export function CardContactItem({
  icon: Icon,
  content,
  value,
  href,
  onClick,
  onTrackClick,
  label,
  type,
  showTooltip = true,
  maxWidth = "max-w-[180px]",
}: CardContactItemProps) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  
  // Use either content or value prop (for backward compatibility)
  const displayContent = content || value || '';
  
  const handleClick = (e: React.MouseEvent) => {
    // Make sure touch targets are big enough on mobile
    if (isMobile) {
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      if (rect.width < 44 || rect.height < 44) {
        // Add additional padding via a parent span if needed
      }
    }
    
    if (onClick) onClick();
    if (onTrackClick) onTrackClick();
  };

  const ContentElement = () => {
    // On mobile, use a more specific width for truncating
    const mobileMaxWidth = isMobile ? "max-w-[140px]" : maxWidth;
    const className = `truncate ${mobileMaxWidth} text-sm text-grandpal-gray ${href ? "hover:text-grandpal-green transition-colors" : ""}`;
    
    if (href) {
      return (
        <a 
          href={href}
          className={className}
          onClick={handleClick}
          // Increase touch target for mobile
          style={isMobile ? { padding: '4px 0' } : undefined}
        >
          {displayContent}
        </a>
      );
    }
    
    return <span className={className}>{displayContent}</span>;
  };
  
  return (
    <div className="flex items-start">
      <Icon className="h-4 w-4 text-grandpal-green mr-2 flex-shrink-0 mt-0.5" aria-hidden="true" />
      {label && <span className="mr-1 text-sm text-grandpal-gray">{label}:</span>}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <ContentElement />
          </TooltipTrigger>
          {showTooltip && (
            <TooltipContent>
              {displayContent}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
